import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getDashboard, getTimeFilter, setTimeFilter } from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mar-26" — the shape every dashboard consumer of the time filter expects. */
const toMonthYear = (value: Date | string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${MONTHS[date.getMonth()]}-${date.getFullYear().toString().slice(-2)}`;
};

const isMonthYear = (value: unknown) =>
  typeof value === 'string' && /^[A-Za-z]{3}-\d{2}$/.test(value.trim());

/**
 * Seeds the dashboard's reporting period.
 *
 * `fetchAllDataValues` only runs once `timeFilter` has a start and an end, and
 * the only thing that ever set it was the `TimeRangeFilter` control. When that
 * control was taken out of the dashboard header the filter stayed empty, so no
 * widget ever requested its values and the whole page rendered as empty frames.
 *
 * Seeding it here keeps the period independent of whether any picker is on
 * screen: the saved range for this model wins, otherwise the model's own plan
 * window is used. Runs only while the filter is unset, so a user's selection is
 * never overwritten.
 */
const useDashboardTimeFilterBootstrap = ({ enable = false }: { enable?: boolean }) => {
  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const model = useSelector(getBlocks);
  const dashboard = useSelector(getDashboard);
  const timeFilter = useSelector(getTimeFilter);

  const hasRange = Boolean(timeFilter?.start && timeFilter?.end);
  const properties = model?.time_properties;
  const startingPeriod = properties?.starting_period;

  useEffect(() => {
    if (!enable || hasRange || !dashboard?.id || !modelId || !startingPeriod) return;

    const saved = (() => {
      try {
        const stored = localStorage.getItem('global-modal-filters');
        const parsed = stored ? JSON.parse(stored) : {};
        return parsed?.[modelId]?.timeFilter ?? {};
      } catch {
        // A malformed entry shouldn't stop the dashboard from loading.
        return {};
      }
    })();

    if (isMonthYear(saved?.startDatePlanPage) && isMonthYear(saved?.endDatePlanPage)) {
      dispatch(setTimeFilter({ start: saved.startDatePlanPage, end: saved.endDatePlanPage }));
      return;
    }

    const granularity = (properties?.time_granularity ?? 'M').toLowerCase();
    const isMonthly = granularity === 'm';
    const periods = Number(properties?.plan_duration_periods);
    const duration = Number.isFinite(periods) ? periods : isMonthly ? 36 : 3;

    const start = new Date(startingPeriod);
    if (Number.isNaN(start.getTime())) return;
    const end = new Date(startingPeriod);
    end.setMonth(end.getMonth() + (isMonthly ? duration : duration * 12));

    const resolvedStart = isMonthly ? toMonthYear(start) : String(start.getFullYear());
    const resolvedEnd = isMonthly ? toMonthYear(end) : String(end.getFullYear());
    if (!resolvedStart || !resolvedEnd) return;

    dispatch(setTimeFilter({ start: resolvedStart, end: resolvedEnd }));
  }, [enable, hasRange, dashboard?.id, modelId, startingPeriod, properties, dispatch]);
};

export default useDashboardTimeFilterBootstrap;
